import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { Reveal, KineticLines } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const interests = [
  "Strategic Communications",
  "Public Relations & Media",
  "Thought Leadership",
  "Brand & Creative Communications",
  "Events & Experiences",
  "Something else",
];

const fieldClass =
  "w-full rounded-none border-0 border-b border-black/25 bg-transparent px-0 py-4 text-base text-foreground placeholder:text-muted-foreground/60 outline-none transition-colors duration-300 focus:border-primary";

export default function Contact() {
  const [form, setForm] = useState({ name: "", organisation: "", email: "", interest: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry received. We shall be in touch shortly.");
      setForm({ name: "", organisation: "", email: "", interest: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <section className="mx-auto max-w-[1600px] px-6 pb-24 pt-40 sm:px-12 sm:pt-52" data-testid="contact-section">
        <p className="mb-8 text-xs uppercase tracking-[0.3em] text-primary">Correspondence</p>
        <KineticLines
          lines={["Let's make it", "matter."]}
          className="font-syne text-[1.75rem] font-extrabold uppercase leading-[1.45] tracking-wider sm:text-[2.4rem] lg:text-[3.9rem]"
        />

        <div className="mt-20 grid grid-cols-1 gap-20 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <p className="max-w-md text-sm leading-relaxed text-muted-foreground sm:text-base">
              Whether the objective is to build a global profile, establish institutional credibility, position
              leadership or create a powerful brand narrative — tell us where you want to go.
            </p>
            <div className="mt-12 space-y-8">
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Email</p>
                <a href="mailto:hello@creative-curve.co.in" data-testid="contact-email-link" className="mt-2 inline-block text-lg text-foreground transition-colors duration-300 hover:text-primary">
                  hello@creative-curve.co.in
                </a>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Focus</p>
                <p className="mt-2 text-sm text-foreground/80">Reputation · Conversations · Leadership · New Markets · Influence · Stories</p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-7">
            <form onSubmit={submit} className="space-y-10" data-testid="contact-form">
              <div className="grid grid-cols-1 gap-10 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Name *</label>
                  <input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} placeholder="Your full name" className={fieldClass} />
                </div>
                <div>
                  <label htmlFor="contact-organisation" className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Organisation</label>
                  <input id="contact-organisation" data-testid="contact-organisation-input" value={form.organisation} onChange={set("organisation")} placeholder="Company or institution" className={fieldClass} />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-10 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-email" className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Email *</label>
                  <input id="contact-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@organisation.com" className={fieldClass} />
                </div>
                <div>
                  <label htmlFor="contact-interest" className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Area of Interest</label>
                  <select id="contact-interest" data-testid="contact-interest-select" value={form.interest} onChange={set("interest")} className={`${fieldClass} cursor-pointer appearance-none`}>
                    <option value="" className="bg-background">Select a discipline</option>
                    {interests.map((i) => (
                      <option key={i} value={i} className="bg-background">{i}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label htmlFor="contact-message" className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Your Message *</label>
                <textarea id="contact-message" data-testid="contact-message-input" required rows={4} value={form.message} onChange={set("message")} placeholder="Tell us about your ambition..." className={`${fieldClass} resize-none`} />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-button"
                className="group flex items-center gap-3 bg-primary px-10 py-4 font-syne text-sm font-bold uppercase tracking-[0.15em] text-primary-foreground transition-colors duration-300 hover:bg-foreground hover:text-background disabled:opacity-50"
              >
                {submitting ? "Despatching..." : "Despatch Enquiry"}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
